import React from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import Modal from 'react-modal';
import i18n from 'i18next';
import {
  getStatus,
} from 'redux-resource';

import config from 'web-config';
import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';
import {
  objectToParams,
} from 'web-utils';

import {
  Checkout,
} from 'web-components';

import {
  Text,
  H200,
} from 'web-styled';

import * as S from './styled';

const stripeParams = objectToParams({
  response_type: 'code',
  client_id: config.stripe.client_id,
  scope: 'read_write',
  redirect_uri: `${window.location.origin}/payments/stripe-connected`,
});

export const StripeOAuth = `https://connect.stripe.com/oauth/authorize${stripeParams}`;

const getCustomStyles = {
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    overflow: 'hidden auto',
    paddingBottom: '50px',
    zIndex: 9999,
    width: 'calc(100% - 230px)',
    top: '64px',
    left: '230px',
  },
  content: {
    padding: 0,
    position: 'relative',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    marginTop: '36px',
    marginBottom: 'auto',
    width: '540px',
    border: 'none',
    outline: 'none',
    overflow: 'visible',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: '15px',
    transform: 'translateX(-115px)',
  },
};

const mapState = state => ({
  profile: coreSelectors.getProfile(state),
  upgradeStatus: getStatus(
    state,
    'paymentIntents.requests.upgradeAccount.status',
  ),
  checkoutModal: getUiState('checkoutModal')(state),
});

const UpgradeModal = () => {
  const dispatch = useDispatch();
  const {
    profile,
    upgradeStatus,
    checkoutModal,
  } = useSelector(mapState, shallowEqual);

  const onUpgrade = () => {
    const onSuccess = ({ id }) => {
      dispatch(
        uiActions.setUiState({
          checkoutModal: {
            planId: id,
            isOpen: true,
          },
        }),
      );
    };

    dispatch(
      coreActions.requestGetUpgradeAccountPlan({
        callbacks: {
          onSuccess,
        },
      }),
    );
  };

  const isPaid = profile.billing_feature || profile.namespace !== 'gymcloud';

  return (
    <>
      <Checkout
        isUpgrade
        closeAfterFinish
        onFinish={() => {
          // temporary solution until user reload page
          dispatch(
            coreActions.setProfileState({
              billing_feature: true,
            }),
          );
          dispatch(
            uiActions.setUiState({
              checkoutModal: {
                isOpen: false,
              },
            }),
          );
        }}
      />
      <Modal
        isOpen={!checkoutModal.isOpen}
        style={getCustomStyles}
        ariaHideApp={false}
        parentSelector={() => document.querySelector('#content')}
      >
        <S.ModalContent>
          <svg width="87" height="94" viewBox="0 0 87 94" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M76.8129 29.9805C81.9562 36.4083 85 44.3777 85 53.0086C85 74.3283 66.4208 91.6105 43.501 91.6105C20.5812 91.6105 2 74.3283 2 53.0086C2 44.4444 4.99793 36.5343 10.0695 30.1306"
              stroke="black"
              strokeWidth="3"
              strokeMiterlimit="10"
              strokeLinejoin="round"
            />
            <path
              d="M9.96924 73.9546V10.3437C9.96924 5.75588 14.005 2 18.9411 2H68.0635C72.9976 2 77.0334 5.75588 77.0334 10.3437V73.9546"
              stroke="black"
              strokeWidth="3"
              strokeMiterlimit="10"
              strokeLinecap="square"
              strokeLinejoin="round"
            />
            <path
              d="M46.85 23.2344C47.0413 23.4566 47.2543 23.5233 47.4715 23.5233C47.8998 23.5233 48.2344 23.2118 48.2344 22.8118C48.2344 22.6563 48.1887 22.5229 48.093 22.3895C47.1587 21.1221 45.5592 20.2549 43.5513 20.1215V18.4316C43.5513 18.1201 43.2624 17.8535 42.9278 17.8535C42.5931 17.8535 42.3083 18.1203 42.3083 18.4316V20.1215C39.2705 20.3437 37.8861 21.9892 37.8861 23.9014C37.8861 29.1711 46.9197 27.2144 46.9197 31.3724C46.9197 33.3291 45.4397 34.3074 42.8102 34.3074C40.7067 34.3074 39.3203 33.3513 38.5554 32.1951C38.4119 31.9729 38.1729 31.8395 37.8861 31.8395C37.4538 31.8395 37.1191 32.151 37.1191 32.5511C37.1191 32.6845 37.1452 32.7732 37.2407 32.9513C38.0056 34.3076 39.8223 35.5306 42.3083 35.664V37.3316C42.3083 37.6431 42.5931 37.9097 42.9278 37.9097C43.2624 37.9097 43.5513 37.6429 43.5513 37.3316V35.664C46.7524 35.4639 48.5472 33.9741 48.5472 31.3726C48.5472 26.014 39.5095 27.5927 39.5095 23.9016C39.5095 22.6342 40.4677 21.478 42.9776 21.478C44.9835 21.478 46.1329 22.4119 46.85 23.2346L46.85 23.2344Z"
              fill="#0074D9"
              stroke="black"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M25.5542 49.7822H62.9039"
              stroke="black"
              strokeWidth="3"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M25.5542 58.354H62.9039"
              stroke="black"
              strokeWidth="3"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M25.5542 66.9233H62.9039"
              stroke="black"
              strokeWidth="3"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <H200 color="#121827">
            {i18n.t(isPaid ? 'upgrade.account.success.title' : 'upgrade.account.title')}
          </H200>
          <Text color="#121827">
            {i18n.t(isPaid ? 'upgrade.account.success.description' : 'upgrade.account.description')}
          </Text>

          {isPaid ? (
            <S.StyledButton onClick={() => { window.location.href = StripeOAuth; }}>
              Connect Account
            </S.StyledButton>
          ) : (
            <S.StyledButton
              onClick={onUpgrade}
              isLoading={upgradeStatus.pending}
              isDisabled={upgradeStatus.pending}
            >
              Upgrade Account
            </S.StyledButton>
          )}

          <Text size="12px" color="#9EA0A1">
            {i18n.t(isPaid ? 'upgrade.account.success.note' : 'upgrade.account.note')}
          </Text>
        </S.ModalContent>
      </Modal>
    </>
  );
};

export default UpgradeModal;
